import React from 'react';
import { Loader2, Upload, Trash2, Users, Send, Lock } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { EmailHistoryModal } from '../../ui/EmailHistoryModal';
import { GuestDetailModal } from './GuestDetailModal';
import { ConvivesFilterBar } from './ConvivesFilterBar';
import { ConvivesGrid } from './ConvivesGrid';
import { ConvivesFilters, EMPTY_FILTERS, Guest, filterGuests } from './guestFilters';

interface ParsedRow { first_name: string; last_name: string; email: string; }

// Un Worker Cloudflare a un temps d'exécution limité — envoyer les 350 emails d'un événement en un seul
// appel serait risqué. Le bouton reste "une seule action" côté commerciale : c'est le navigateur qui
// enchaîne les lots, pas elle qui doit cliquer 350 fois.
const SEND_BATCH_SIZE = 25;

// Détection tolérante des colonnes, même principe que le parseur de référence de l'app
// (src/server/app.ts:376-416, repris ici côté navigateur avec xlsx) — accepte un CSV ou un Excel, avec des
// en-têtes en français ou en anglais. Le ﻿ (BOM) est retiré explicitement : un CSV réenregistré depuis
// un éditeur de texte Windows (Notepad) est souvent sauvé en "UTF-8 avec BOM", qui se colle invisiblement au
// tout premier en-tête ("prénom" étant la première colonne) et l'empêchait de correspondre malgré un texte
// identique à l'œil — bug remonté par Emeni (les prénoms n'apparaissaient jamais dans l'aperçu).
const cleanKey = (k: string) => k.replace(/^\uFEFF/, '').trim().toLowerCase();
const findCol = (keys: string[], ...candidates: string[]) => keys.find((k) => candidates.some((c) => cleanKey(k) === c.toLowerCase())) ?? null;

/** Import du fichier de convives transmis par l'organisateur (Phase 3 de "Menus & Convives") — prénom/nom/
 *  email uniquement, aucune autre donnée collectée à ce stade. Le fichier est entièrement parsé côté
 *  navigateur (jamais envoyé brut au serveur) ; seules les lignes déjà nettoyées repartent en JSON, sur le
 *  même principe que l'import Excel déjà utilisé ailleurs dans l'app. */
export function ConvivesImportSection({ eventId }: { eventId: string }) {
    const [guests, setGuests] = React.useState<Guest[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [parsedRows, setParsedRows] = React.useState<ParsedRow[]>([]);
    const [parseError, setParseError] = React.useState('');
    const [importing, setImporting] = React.useState(false);
    const [importResult, setImportResult] = React.useState<{ imported: number; duplicates_skipped: number; invalid_skipped: number } | null>(null);
    const [responsesClosedAt, setResponsesClosedAt] = React.useState<string | null>(null);

    const load = React.useCallback(() => {
        moduleApi.getEvenementielGuests(eventId).then((g: any) => setGuests(Array.isArray(g) ? g : [])).catch(() => setGuests([])).finally(() => setLoading(false));
        moduleApi.getEvenementielCollecte(eventId).then((col: any) => setResponsesClosedAt(col?.responses_closed_at || null)).catch(() => {});
    }, [eventId]);

    React.useEffect(() => { load(); }, [load]);

    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setParseError('');
        setImportResult(null);
        setParsedRows([]);
        file.arrayBuffer().then(async (buf) => {
            const XLSX = await import('xlsx');
            const workbook = XLSX.read(new Uint8Array(buf), { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            if (rows.length === 0) { setParseError('Le fichier est vide.'); return; }
            const keys = Object.keys(rows[0]);
            const colEmail = findCol(keys, 'email', 'e-mail', 'mail', 'courriel');
            if (!colEmail) { setParseError("Colonne email introuvable dans le fichier."); return; }
            const colNom = findCol(keys, 'nom', 'last_name', 'lastname', 'name');
            // "prénom" est la colonne la plus fragile à faire correspondre par nom exact : un accent mal
            // encodé selon l'éditeur/le logiciel utilisé pour produire le fichier peut le rendre méconnaissable
            // alors que "nom"/"email" (sans accent) passent sans problème — bug remonté par Emeni malgré un
            // premier correctif (BOM). Repli robuste : s'il ne reste plus qu'UNE seule colonne non réclamée par
            // nom/email, on la prend pour le prénom, quel que soit son libellé exact.
            let colPrenom = findCol(keys, 'prénom', 'prenom', 'first_name', 'firstname');
            if (!colPrenom) {
                const unclaimed = keys.filter((k) => k !== colNom && k !== colEmail);
                if (unclaimed.length === 1) colPrenom = unclaimed[0];
            }
            setParsedRows(rows.map((r) => ({
                first_name: colPrenom ? String(r[colPrenom] || '').trim() : '',
                last_name: colNom ? String(r[colNom] || '').trim() : '',
                email: String(r[colEmail] || '').trim(),
            })));
        }).catch(() => setParseError('Impossible de lire ce fichier.'));
    };

    const removeParsedRow = (idx: number) => setParsedRows((prev) => prev.filter((_, i) => i !== idx));

    const confirmImport = async () => {
        setImporting(true);
        setParseError('');
        try {
            const res: any = await moduleApi.importEvenementielGuests(eventId, parsedRows);
            setImportResult(res);
            setParsedRows([]);
            load();
        } catch (e: any) {
            setParseError(e?.message || "Erreur lors de l'import.");
        } finally {
            setImporting(false);
        }
    };

    const removeGuest = async (guestId: string) => {
        if (!window.confirm('Retirer ce convive de la liste ?')) return;
        try {
            await moduleApi.deleteEvenementielGuest(eventId, guestId);
            load();
        } catch (e: any) {
            setParseError(e?.message || 'Erreur lors de la suppression.');
        }
    };

    const [sending, setSending] = React.useState(false);
    const [sendProgress, setSendProgress] = React.useState<{ done: number; total: number; sent: number; failed: number; lastError: string } | null>(null);

    const sendBatch = async (guestIds: string[]) => {
        setSending(true);
        setParseError('');
        const totals = { done: 0, total: guestIds.length, sent: 0, failed: 0, lastError: '' };
        setSendProgress({ ...totals });
        for (let i = 0; i < guestIds.length; i += SEND_BATCH_SIZE) {
            const batch = guestIds.slice(i, i + SEND_BATCH_SIZE);
            try {
                const res: any = await moduleApi.sendEvenementielInvitations(eventId, batch);
                totals.sent += res?.sent || 0;
                totals.failed += res?.failed || 0;
                if (res?.last_error) totals.lastError = res.last_error;
            } catch (e: any) {
                totals.failed += batch.length;
            }
            totals.done += batch.length;
            setSendProgress({ ...totals });
        }
        setSending(false);
        load();
    };

    const sendAllInvitations = () => sendBatch(guests.filter((g) => !g.invitation_sent_at).map((g) => g.id));
    const remindUnresponded = () => sendBatch(guests.filter((g) => g.invitation_sent_at && !g.responded_at).map((g) => g.id));
    const resendOne = (guestId: string) => sendBatch([guestId]);

    const notYetInvitedCount = guests.filter((g) => !g.invitation_sent_at).length;
    const unrespondedCount = guests.filter((g) => g.invitation_sent_at && !g.responded_at).length;
    const nonRespondedCount = guests.filter((g) => !g.responded_at).length;

    // "Clôturer & Valider" — fusion de deux actions (Demande explicite d'Emeni, 2026-08-24) :
    // 1. Ferme les réponses (responses_closed_at) : bloque non-répondants de répondre
    // 2. Valide/verrouille (production_locked_at) : empêche TOUS les convives de modifier après jour J
    // Les convives non-répondants reçoivent un email configurable les orientant vers l'organisateur.
    // Demande explicite d'Emeni (2026-08-23) : "on devrait avoir un bouton clôturer, et là tous les convives
    // qui n'ont pas répondu reçoivent un mail configurable".
    const [closing, setClosing] = React.useState(false);
    const closeResponses = async () => {
        if (!window.confirm(`Clôturer les réponses et valider ? ${nonRespondedCount} convive(s) n'ayant jamais répondu recevront un email. Cette action est définitive — les convives ne pourront plus modifier leur choix.`)) return;
        setClosing(true);
        setParseError('');
        try {
            await moduleApi.closeEvenementielCollecte(eventId);
            await moduleApi.validateEvenementielCollecte(eventId);
            const nonResponded = guests.filter((g) => !g.responded_at).map((g) => g.id);
            const totals = { done: 0, total: nonResponded.length, sent: 0, failed: 0, lastError: '' };
            setSendProgress({ ...totals });
            for (let i = 0; i < nonResponded.length; i += SEND_BATCH_SIZE) {
                const batch = nonResponded.slice(i, i + SEND_BATCH_SIZE);
                try {
                    const res: any = await moduleApi.closeNotifyEvenementielConvives(eventId, batch);
                    totals.sent += res?.sent || 0;
                    totals.failed += res?.failed || 0;
                    if (res?.last_error) totals.lastError = res.last_error;
                } catch (e: any) {
                    totals.failed += batch.length;
                }
                totals.done += batch.length;
                setSendProgress({ ...totals });
            }
        } catch (e: any) {
            setParseError(e?.message || 'Erreur lors de la clôture et validation.');
        } finally {
            setClosing(false);
            load();
        }
    };

    const [historyGuest, setHistoryGuest] = React.useState<Guest | null>(null);
    const [detailGuest, setDetailGuest] = React.useState<Guest | null>(null);

    // Recherche + filtres combinables — indispensable dès que la liste dépasse quelques dizaines de convives
    // (ex: 350 pour un grand événement). Demande initiale d'Emeni (2026-08-23) : "il faudrait ajouter une
    // barre de recherche pour les convives, et un filtre pour pouvoir filtrer par ceux qui n'ont pas répondu",
    // étendue le 2026-08-24 aux allergies, restrictions et à l'alerte "n'a pas choisi de plat". La logique de
    // filtrage vit dans convives/guestFilters.ts, ce composant ne fait qu'en porter l'état.
    const [filters, setFilters] = React.useState<ConvivesFilters>(EMPTY_FILTERS);

    const filteredGuests = React.useMemo(() => filterGuests(guests, filters), [guests, filters]);

    return (
        <div className="bg-surface-soft border border-border rounded-2xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-content flex items-center gap-2"><Users size={15} /> Convives ({guests.length})</h3>

            <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-surface border border-border text-sm text-content-muted hover:text-content cursor-pointer transition-colors w-fit">
                <Upload size={14} />
                <span>Importer un fichier (CSV ou Excel — prénom, nom, email)</span>
                <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFile} />
            </label>

            {parseError && <p className="text-xs text-red-500 font-semibold">{parseError}</p>}
            {importResult && (
                <p className="text-xs text-content-muted">
                    {importResult.imported} importé(s)
                    {importResult.duplicates_skipped > 0 && ` · ${importResult.duplicates_skipped} déjà présent(s) ignoré(s)`}
                    {importResult.invalid_skipped > 0 && ` · ${importResult.invalid_skipped} ligne(s) sans email ignorée(s)`}
                </p>
            )}

            {parsedRows.length > 0 && (
                <div className="space-y-2">
                    <div className="max-h-56 overflow-y-auto space-y-1 rounded-lg border border-border p-1.5 bg-surface">
                        {parsedRows.map((r, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-2 px-2 py-1 text-xs text-content">
                                <span className="truncate">{r.first_name} {r.last_name} — {r.email || <em className="text-red-400">email manquant</em>}</span>
                                <button onClick={() => removeParsedRow(idx)} className="text-content-muted hover:text-red-400 shrink-0"><Trash2 size={12} /></button>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-end">
                        <button onClick={confirmImport} disabled={importing}
                            className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-40">
                            {importing ? 'Import...' : `Importer ces ${parsedRows.length} ligne(s)`}
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-4"><Loader2 className="animate-spin text-content-muted" size={18} /></div>
            ) : guests.length > 0 && (
                <>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={sendAllInvitations} disabled={sending || notYetInvitedCount === 0}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-40">
                            <Send size={12} /> {notYetInvitedCount > 0 ? `Envoyer les invitations (${notYetInvitedCount})` : 'Toutes les invitations sont envoyées'}
                        </button>
                        <button onClick={remindUnresponded} disabled={sending || unrespondedCount === 0}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface border border-border text-content-muted hover:text-content font-bold text-xs disabled:opacity-40">
                            <Send size={12} /> Relancer les non-répondants{unrespondedCount > 0 ? ` (${unrespondedCount})` : ''}
                        </button>
                        {responsesClosedAt ? (
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 font-bold text-xs">
                                <Lock size={12} /> Réponses clôturées
                            </span>
                        ) : (
                            <button onClick={closeResponses} disabled={closing || sending || nonRespondedCount === 0}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 text-red-500 hover:bg-red-500/10 font-bold text-xs disabled:opacity-40">
                                <Lock size={12} /> Clôturer{nonRespondedCount > 0 ? ` (${nonRespondedCount})` : ''}
                            </button>
                        )}
                    </div>
                    {sendProgress && (
                        <p className="text-[11px] text-content-muted">
                            {sendProgress.done}/{sendProgress.total} traités — {sendProgress.sent} envoyé(s){sendProgress.failed > 0 ? `, ${sendProgress.failed} échoué(s)` : ''}
                            {sendProgress.failed > 0 && sendProgress.lastError && <span className="block text-red-500 font-semibold mt-0.5">Dernière erreur : {sendProgress.lastError}</span>}
                        </p>
                    )}
                    <ConvivesFilterBar guests={guests} filters={filters} onChange={setFilters} resultCount={filteredGuests.length} />
                    <ConvivesGrid
                        guests={filteredGuests}
                        resetKey={JSON.stringify(filters)}
                        sending={sending}
                        onOpen={(g) => setDetailGuest(g)}
                        onSendInvitation={(g) => resendOne(g.id)}
                        onShowHistory={(g) => setHistoryGuest(g)}
                        onDelete={(g) => removeGuest(g.id)}
                    />
                </>
            )}

            {historyGuest && (
                <EmailHistoryModal
                    title={`Historique — ${historyGuest.first_name} ${historyGuest.last_name}`}
                    fetchHistory={async () => {
                        const res: any = await moduleApi.getEvenementielGuestEmailHistory(eventId, historyGuest.id);
                        return Array.isArray(res?.emails) ? res.emails : [];
                    }}
                    onClose={() => setHistoryGuest(null)}
                />
            )}

            {detailGuest && (
                <GuestDetailModal eventId={eventId} guestId={detailGuest.id} onClose={() => setDetailGuest(null)} />
            )}
        </div>
    );
}
